<template>
  <div>
    <template v-if="isButton">
      <div v-if="useArrowButton"
           class=
           "btn btn-secondary btn-lg btn-block d-flex justify-content-between align-items-center"
           v-b-modal="modalId">
        <span class="text-white">{{ searchButtonText }}</span>
        <img src="@/assets/ButtonArrow.svg" height="25" alt="arrow button">
      </div>
      <b-button v-else size="sm" v-b-modal="modalId">{{ searchButtonText }}</b-button>
    </template>
    <template v-else>
      <span class="text-secondary" v-b-modal="modalId">
        {{ searchButtonText }}
      </span>
    </template>
    <b-modal ref="contactSearch"
      :id=modalId
      centered
      size="lg"
      title="Search Contacts"
      no-close-on-backdrop
      @show="refreshExisting"
    >
      <div v-if="!disableExisting" class="d-flex justify-content-between">
        <span class="label-text form-text mr-3">
          Search Mode
        </span>
        <b-form-radio-group
          v-model="searchMode"
          buttons
          button-variant="outline-primary"
          class="d-flex flex-grow-1 bg-white mb-3"
          size="sm"
        >
          <b-form-radio :class="'flex-even c-pointer'" :value="false">
            Search eTapestry
          </b-form-radio>
          <b-form-radio :class="'flex-even c-pointer'" :value="true">
            Pick from Current Project Contacts
          </b-form-radio>
        </b-form-radio-group>
      </div>
      <form @submit.prevent
        class="needs-validation contactSearchForm"
        autocomplete="off"
        novalidate
        v-if="!searchMode"
      >
        <div v-if="serverError" class="alert alert-danger" role="alert">
          {{serverError}}
        </div>
        <p class="mb-2">
          Start typing the Contact Name and/or Address to view results.
          Click a result to select the Contact, then click Use Selected Contact to use the
          Contact on this form.
        </p>
        <div class="form-row form-group row">
          <label for="name" class="label-text col-lg-4">
            <span class="text-danger">*</span>Name and/or Address
          </label>
          <div class="col-lg-8">
            <input v-validate="'required'"
              data-vv-as="Name and/or Address"
              type="text"
              class="form-control"
              name="name"
              v-model="contactSearch.name"
              @input="onSubmit"
              placeholder="Enter Contact Name and/or Address"
            />
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
          </div>
        </div>
        <ul class="list-group border border-dark rounded results noselect contact-list">
          <LoadingSpinner v-if="submitting" message="Loading Search Results"  size = '1x'>
          </LoadingSpinner>
          <div v-if="!submitting">
            <div v-if="searchResult.length == 0"
              class="d-flex align-items-center justify-content-center placeholder p-2">
              <div v-if="contactSearch.name.length > 3">
                <p>
                  No match for <b>{{ contactSearch.name }}</b>
                  was found. Please check your entry and add a new contact
                  in&nbsp;<a class="text-secondary hyperlink"
                    href="https://bos.etapestry.com/prod/Login.jsp?application=enum.applicationType.etapestry"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                  <u>eTapestry</u></a>, if needed.
                </p>
              </div>
              <div v-else>
                Search above for results
              </div>
            </div>
            <li v-for="result in searchResult"
              :key="result.crmId"
              class="list-group-item result"
              :class="{ active : activeSearchResultId == result.crmId}"
              @click="selectContact(result)">
              <div class="row">
                <div class="col-lg-5 mb-2 row">
                  <div class="col-lg-4 label-text">Name</div>
                  <div class="col-lg-8">{{ result | contactName }}</div>
                </div>
                <div class="col-lg-7 mb-2 row">
                  <div class="col-lg-3 label-text">
                    Address
                  </div>
                  <div class="col-lg-9" v-html="$options.filters.contactAddress(result)">
                  </div>
                </div>
              </div>
            </li>
          </div>
        </ul>
      </form>
      <template v-if="searchMode">
        <LoadingSpinner v-if="existingContactsLoading"
          message="Loading Existing Contacts"
          size = '1x'
        />
        <div v-if="!existingContactsLoading && !uniqueExistingContacts.length">
          No Contacts exist on this Project that are not already added to this
          {{getParentType(parentType)}}.
        </div>
        <select v-if="!existingContactsLoading && uniqueExistingContacts.length"
          v-model="existingContactId"
          class="form-control"
          name="existingContact"
        >
          <option :value="null" disabled>Pick a Current Project Contact</option>
          <option v-for="contact in uniqueExistingContacts"
            :value="contact.crmId"
            :key="contact.crmId"
          >
            {{ contact.crmContact | contactName }}
          </option>
        </select>
      </template>
      <div>
        <div v-if="!syncingWithETapestry && lastSync">
          <div class="center-text placeholder">
            Last synced with eTapestry at {{ lastSync | dateTimeString }}
          </div>
          <div class="center-text placeholder">
            Don't see recent updates?
            Click&nbsp;<a class="text-secondary hyperlink"
              href="#"
              @click="syncCrm(true)"><u>here</u></a>&nbsp;to resync the data.
          </div>
          <div class="center-text placeholder">
            To add a new contact or edit an existing contact, click
            <a href="https://bos.etapestry.com/prod/Main2.jsp"
               class="text-secondary hyperlink"
               target="_blank"><u>here</u></a>
            to open eTapestry.
          </div>
        </div>
        <LoadingSpinner v-if="syncingWithETapestry" message="Syncing with eTapestry" size='0.5x'>
        </LoadingSpinner>
      </div>
      <div slot="modal-footer" class="d-flex justify-content-center w-100">
        <b-button variant="danger" class="button-row-spacing" @click="hideModal">Cancel</b-button>
        <b-button class="button-row-spacing"
          @click="useSelectedContact"
          :disabled="(!searchMode && activeSearchResultId == null) ||
          (searchMode && existingContactId == null)"
        >
          Use Selected Contact
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { EntityModules } from '@/store/moduleConfig';
import api from '@/api';
import LoadingSpinner from '../LoadingSpinner.vue';

export default {
  name: 'SearchContactsModal',
  data() {
    return {
      serverError: null,
      contactSearch: {
        name: '',
      },
      searchMode: false,
      searchResult: [],
      submitting: false,
      existingContactId: null,
      activeSearchResultId: null,
      lastSync: null,
    };
  },
  components: {
    LoadingSpinner,
  },
  props: {
    modalId: {
      type: String,
    },
    parentType: {
      type: String,
    },
    parentId: {
      type: Number,
    },
    disableExisting: {
      type: Boolean,
    },
    isButton: {
      type: Boolean,
      default() {
        return true;
      },
    },
    useArrowButton: {
      type: Boolean,
      default() {
        return false;
      },
    },
    searchButtonText: {
      type: String,
      default() {
        return 'Search Contacts';
      },
    },
  },
  mounted() {
    this.syncCrm();
  },
  computed: {
    ...mapState(EntityModules.PROJECT_CONTACT.namespace, {
      existingContacts: state => state.entities,
      existingContactsLoading: state => state.loading,
    }),
    ...mapState('Common', {
      syncingWithETapestry: state => state.syncingWithETapestry,
    }),
    uniqueExistingContacts() {
      const newContactsOnly = this.existingContacts
        .filter(contact => !this.uniqueParentCrmIds().has(contact.crmId));

      const uniqueContacts = newContactsOnly.reduce((accumulator, contact) => {
        accumulator.set(contact.crmId, contact);
        return accumulator;
      }, new Map());

      return Array.from(uniqueContacts.values());
    },
  },
  methods: {
    refreshExisting() {
      this.$store.dispatch(`${EntityModules.PROJECT_CONTACT.namespace}/fetchEntities`, {
        parentType: EntityModules.PROJECT_CONTACT.parentType,
        parentId: parseInt(this.$route.params.project_id, 10),
        entityType: EntityModules.PROJECT_CONTACT.entityType,
        forceLoad: true,
      });
    },
    hideModal() {
      this.$refs.contactSearch.hide();
    },
    uniqueParentCrmIds() {
      if (!this.parentType) {
        return new Set();
      }
      const parentCrmIds = this.existingContacts
        .filter(contact => this.parentId === contact[`${this.parentType}Id`])
        .map(contact => contact.crmId);

      const uniqueParentCrmIds = new Set(...[parentCrmIds]);
      return uniqueParentCrmIds;
    },
    uniqueSearchedContacts(searchResults) {
      const filteredContacts = searchResults.slice()
        .filter(c => !this.uniqueParentCrmIds().has(c.crmId));
      return filteredContacts;
    },
    onSubmit() {
      this.refreshExisting();
      if (this.contactSearch.name && this.contactSearch.name.length > 2) {
        this.$validator.validate()
          .then((result) => {
            this.submitting = true;
            if (result) {
              api.get(`/contact/crmSearch?name=${this.contactSearch.name}`)
                .then(({ data }) => {
                  this.serverError = null;
                  this.activeSearchResultId = null;
                  this.searchResult = this.uniqueSearchedContacts(data);
                  this.submitting = false;
                }).catch((error) => {
                  this.serverError = error.response.data;
                });
            }
          });
      } else {
        this.searchResult = [];
      }
    },
    syncCrm(forceSync = false) {
      this.$store.dispatch('Common/startETapestrySync');
      api.post(`/contact/syncCrm?forceSync=${forceSync}`)
        .then(({ data }) => {
          this.onSubmit();
          this.serverError = data.serverErrorOnSubsequentCall;
          this.$store.dispatch('Common/stopETapestrySync');
          this.lastSync = data.createdAt;
        }).catch((error) => {
          this.serverError = error.response.data;
          this.$store.dispatch('Common/stopETapestrySync');
        });
    },
    selectContact(contactResult) {
      if (this.activeSearchResultId !== contactResult.crmId) {
        this.activeSearchResultId = contactResult.crmId;
      } else {
        this.activeSearchResultId = null;
      }
    },
    useSelectedContact() {
      if (!this.searchMode) {
        const contact = this.searchResult
          .find(result => result.crmId === this.activeSearchResultId);
        this.$emit('set-contact', contact);
        this.hideModal();
      } else {
        const contact = this.existingContacts
          .find(existingContact => existingContact.crmId === this.existingContactId);
        this.$emit('set-contact', contact.crmContact);
        this.hideModal();
      }
      this.contactSearch = { name: '' };
      this.searchResult = [];
    },
    getParentType(parentType) {
      if (parentType === 'instrument') {
        return 'Instrument';
      }
      if (parentType === 'property') {
        return 'Property';
      }
      return 'Service';
    },
  },
};
</script>
